import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
// import { gptChatService } from 'features/chat/agent-chat-module/services/gpt-chat.service';

const POPUP_AUTO_CLOSE_MS = 2000;

type CallbackStatus = 'processing' | 'success' | 'error';

type ComposioOauthMessage = {
  type: 'composio-oauth';
  ok: boolean;
  state?: string | null;
  status?: string | null;
  connectedAccountId?: string | null;
  toolId?: string | null;
  toolkit?: string | null;
  error?: string | null;
};

function notifyOpener(message: Omit<ComposioOauthMessage, 'type'>) {
  if (!window.opener || window.opener === window || window.opener.closed) {
    return;
  }

  try {
    window.opener.postMessage(
      {
        type: 'composio-oauth',
        ...message,
      },
      window.location.origin
    );
  } catch {
    // Opener may block messaging; connections polling is the fallback.
  }
}

// function extractHtmlMessage(html: string): string | null {
//   const bodyMatch = /<body[^>]*>([\s\S]*?)<\/body>/i.exec(html);
//   const source = bodyMatch?.[1] ?? html;
//   const withoutScripts = source.replace(/<script[\s\S]*?<\/script>/gi, ' ');
//   const text = withoutScripts
//     .replace(/<[^>]+>/g, ' ')
//     .replace(/\s+/g, ' ')
//     .trim();
//
//   return text.length > 0 ? text : null;
// }

export default function ComposioOauthCallbackPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [viewStatus, setViewStatus] = useState<CallbackStatus>('processing');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isPopup] = useState(() => Boolean(window.opener && window.opener !== window));

  const query = useMemo(() => {
    const entries: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      entries[key] = value;
    });
    return entries;
  }, [searchParams]);

  const hasRequiredParams = Boolean(query.state && query.status && query.connected_account_id);
  const urlStatusSuccess = query.status?.toLowerCase() === 'success';

  useLayoutEffect(() => {
    if (!hasRequiredParams) {
      setViewStatus('error');
      setErrorMessage(
        t('MISSING_OAUTH_CALLBACK_PARAMS', {
          defaultValue: 'Missing OAuth callback parameters.',
        })
      );
      return;
    }

    // API-based success/error UI — kept for reference; callback result now comes from URL params.
    // const run = async () => {
    //   try {
    //     const response = await gptChatService.completeComposioOauthCallback(query);
    //     const apiSucceeded = response.ok && Boolean(response.html);
    //
    //     if (urlStatusSuccess && apiSucceeded) {
    //       notifyOpener({
    //         ok: true,
    //         state: query.state ?? null,
    //         status: query.status ?? null,
    //         connectedAccountId: query.connected_account_id ?? null,
    //         toolId: response.toolId ?? null,
    //         toolkit: response.toolkit ?? null,
    //       });
    //
    //       setSuccessMessage(
    //         extractHtmlMessage(response.html!) ||
    //           (isPopup
    //             ? t('COMPOSIO_CALLBACK_POPUP_SUCCESS', {
    //                 defaultValue:
    //                   'Connection is being completed in the main window. You can close this window.',
    //               })
    //             : t('COMPOSIO_CALLBACK_SUCCESS', {
    //                 defaultValue:
    //                   'Your account was connected successfully. You can return to tools.',
    //               }))
    //       );
    //       setViewStatus('success');
    //       return;
    //     }
    //
    //     const detail =
    //       response.detail ||
    //       (!urlStatusSuccess
    //         ? t('OAUTH_CALLBACK_NOT_SUCCESSFUL', {
    //             defaultValue: 'The authorization did not complete successfully.',
    //           })
    //         : t('COMPOSIO_CONNECTION_FAILED', {
    //             defaultValue: 'The authorization did not complete.',
    //           }));
    //
    //     notifyOpener({
    //       ok: false,
    //       state: query.state ?? null,
    //       status: query.status ?? null,
    //       connectedAccountId: query.connected_account_id ?? null,
    //       error: detail,
    //     });
    //
    //     setErrorMessage(detail);
    //     setViewStatus('error');
    //   } catch (error) {
    //     const detail =
    //       error instanceof Error
    //         ? error.message
    //         : t('COMPOSIO_CONNECTION_FAILED', {
    //             defaultValue: 'The authorization did not complete.',
    //           });
    //
    //     notifyOpener({
    //       ok: false,
    //       state: query.state ?? null,
    //       status: query.status ?? null,
    //       error: detail,
    //     });
    //
    //     setErrorMessage(detail);
    //     setViewStatus('error');
    //   }
    // };
    // void run();

    if (urlStatusSuccess) {
      notifyOpener({
        ok: true,
        state: query.state ?? null,
        status: query.status ?? null,
        connectedAccountId: query.connected_account_id ?? null,
      });

      setSuccessMessage(
        isPopup
          ? t('COMPOSIO_CALLBACK_POPUP_SUCCESS', {
              defaultValue:
                'Connection is being completed in the main window. You can close this window.',
            })
          : t('COMPOSIO_CALLBACK_SUCCESS', {
              defaultValue: 'Your account was connected successfully. You can return to tools.',
            })
      );
      setViewStatus('success');
      return;
    }

    const detail = t('OAUTH_CALLBACK_NOT_SUCCESSFUL', {
      defaultValue: 'The authorization did not complete successfully.',
    });

    notifyOpener({
      ok: false,
      state: query.state ?? null,
      status: query.status ?? null,
      connectedAccountId: query.connected_account_id ?? null,
      error: detail,
    });

    setErrorMessage(detail);
    setViewStatus('error');
  }, [hasRequiredParams, isPopup, query, t, urlStatusSuccess]);

  useEffect(() => {
    if (viewStatus === 'success') {
      const timer = window.setTimeout(() => {
        window.close();
        if (!isPopup) {
          navigate('/tools?tab=composio', { replace: true });
        }
      }, POPUP_AUTO_CLOSE_MS);

      return () => window.clearTimeout(timer);
    }

    if (!isPopup || viewStatus !== 'error') {
      return;
    }

    const timer = window.setTimeout(() => {
      window.close();
    }, POPUP_AUTO_CLOSE_MS);

    return () => window.clearTimeout(timer);
  }, [isPopup, navigate, viewStatus]);

  const cardClassName = 'w-full max-w-md rounded-lg border border-border bg-card p-8';

  if (viewStatus === 'processing') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className={cardClassName}>
          <div className="flex flex-col items-center gap-4 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <h2 className="text-xl font-semibold text-foreground">
              {t('PROCESSING_AUTHORIZATION', { defaultValue: 'Processing authorization' })}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('COMPOSIO_CALLBACK_PROCESSING', {
                defaultValue: 'Please wait while we finish connecting your account…',
              })}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (viewStatus === 'success') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className={cardClassName}>
          <div className="flex flex-col items-center gap-4 text-center">
            <CheckCircle className="h-12 w-12 text-success-green" />
            <h2 className="text-xl font-semibold text-foreground">
              {t('AUTHORIZATION_SUCCESSFUL', { defaultValue: 'Authorization successful' })}
            </h2>
            <p className="text-sm text-muted-foreground">{successMessage}</p>
            {isPopup ? (
              <>
                <p className="text-xs text-muted-foreground">
                  {t('COMPOSIO_CALLBACK_MAIN_TAB_UPDATED', {
                    defaultValue:
                      'The connection status in the main tab will update automatically.',
                  })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('COMPOSIO_CALLBACK_AUTO_CLOSE', {
                    defaultValue: 'This window will close automatically in 2 seconds.',
                  })}
                </p>
              </>
            ) : null}
            {/* <Button
              className="mt-2"
              type="button"
              onClick={() => navigate('/tools?tab=composio', { replace: true })}
            >
              {t('RETURN_TO_TOOLS', { defaultValue: 'Return to Tools' })}
            </Button> */}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className={cardClassName}>
        <div className="flex flex-col items-center gap-4 text-center">
          <XCircle className="h-12 w-12 text-destructive" />
          <h2 className="text-xl font-semibold text-foreground">
            {t('CONNECTION_FAILED', { defaultValue: 'Connection failed' })}
          </h2>
          <p className="text-sm text-muted-foreground">
            {errorMessage ||
              t('OAUTH_CALLBACK_ERROR', {
                defaultValue: 'An error occurred while completing the connection.',
              })}
          </p>
          {isPopup ? (
            <>
              <p className="text-xs text-muted-foreground">
                {t('COMPOSIO_CALLBACK_AUTO_CLOSE', {
                  defaultValue: 'This window will close automatically in 2 seconds.',
                })}
              </p>
            </>
          ) : (
            <Button
              className="mt-2"
              type="button"
              onClick={() => navigate('/tools?tab=composio', { replace: true })}
            >
              {t('RETURN_TO_TOOLS', { defaultValue: 'Return to Tools' })}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
